import { useEffect, useRef } from "react";
import { useRouterState } from "@tanstack/react-router";
import { captureAttribution } from "@/lib/attribution";
import { track, trackLead, trackPageView } from "@/lib/analytics";
import { isHuggyReady, openHuggy } from "@/lib/huggy";
import {
  startTelemetry,
  tCta,
  tHuggy,
  tPageView,
  tScroll,
  tSection,
  tWhatsapp,
} from "@/lib/telemetry";

/** Tira o texto pré-preenchido de um link wa.me / api.whatsapp.com. */
function messageFromWaHref(href: string): string {
  try {
    const u = new URL(href);
    const t = u.searchParams.get("text") || "";
    // remove o rodapé "— origem: ..." (no Huggy a origem vai nos campos do contato)
    return t.split("\n\n— origem:")[0].trim();
  } catch {
    return "";
  }
}

const WA_HOSTS = ["wa.me", "whatsapp.com", "api.whatsapp.com"];

/**
 * Cola gtag + telemetria própria na navegação da SPA:
 *  - captura a origem (Ads/UTM) no primeiro load
 *  - page_view / seções vistas / scroll depth
 *  - lead em qualquer clique de link do WhatsApp
 */
export function TagManager() {
  const path = useRouterState({
    select: (s) => s.location.pathname + (s.location.searchStr || ""),
  });
  const first = useRef(true);

  useEffect(() => {
    captureAttribution();
    startTelemetry();
  }, []);

  // page_view a cada rota
  useEffect(() => {
    if (first.current) {
      first.current = false;
      tPageView(path); // gtag já mandou o inicial; a telemetria própria não
      return;
    }
    trackPageView(path);
    tPageView(path);
  }, [path]);

  // seções vistas (uma vez por sessão) via IntersectionObserver
  useEffect(() => {
    const seen = new Set<string>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const en of entries) {
          if (!en.isIntersecting) continue;
          const name = (en.target as HTMLElement).dataset.trackSection;
          if (name && !seen.has(name)) {
            seen.add(name);
            tSection(name);
          }
        }
      },
      { threshold: 0.4 },
    );
    const attach = () =>
      document.querySelectorAll("[data-track-section]").forEach((el) => io.observe(el));
    attach();
    // re-observa quando a rota muda (novas seções montadas)
    const t = setTimeout(attach, 300);
    return () => {
      clearTimeout(t);
      io.disconnect();
    };
  }, [path]);

  // scroll depth 25/50/75/100
  useEffect(() => {
    const marks = [25, 50, 75, 100];
    let hit = 0;
    const onScroll = () => {
      const doc = document.documentElement;
      const pct =
        ((window.scrollY + window.innerHeight) / (doc.scrollHeight || 1)) * 100;
      while (hit < marks.length && pct >= marks[hit]) {
        tScroll(marks[hit]);
        hit++;
      }
      if (hit >= marks.length) window.removeEventListener("scroll", onScroll);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [path]);

  // cliques de WhatsApp / CTA
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      const node = el?.closest?.("a[href], button") as HTMLElement | null;
      if (!node) return;

      const ctaLoc =
        node.getAttribute("data-track-cta") ||
        node.closest("[data-track-cta]")?.getAttribute("data-track-cta");

      const a = node.matches("a[href]") ? (node as HTMLAnchorElement) : null;
      let host = "";
      if (a) {
        try {
          host = new URL(a.href).host.replace(/^www\./, "");
        } catch {
          /* ignore */
        }
      }
      const isWa = !!a && WA_HOSTS.includes(host);

      if (!isWa) {
        if (ctaLoc) tCta(ctaLoc);
        return;
      }

      const loc =
        node.getAttribute("data-wa-loc") ||
        node.closest("[data-wa-loc]")?.getAttribute("data-wa-loc") ||
        window.location.pathname;
      const plan = node.getAttribute("data-wa-plan") || undefined;

      // Evento simples de clique (conversão do Google Ads), antes de abrir o WhatsApp/chat.
      track("click_whatsapp", {
        link_text: (node.textContent || node.getAttribute("aria-label") || "WhatsApp")
          .replace(/\s+/g, " ")
          .trim()
          .slice(0, 100),
        link_url: a!.href,
        contact_location: loc,
      });

      if (isHuggyReady() && openHuggy(messageFromWaHref(a!.href))) {
        e.preventDefault();
        trackLead({ channel: "chat", location: loc, plan });
        tHuggy(loc);
        return;
      }
      trackLead({ channel: "whatsapp", location: loc, plan });
      tWhatsapp(loc);
    };
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  return null;
}
